import { Popover, Rate, Whisper, WhisperInstance } from "rsuite";
import { ReactNode, useRef, MouseEvent } from "react";
import { Button } from "reactstrap";

import useUpdateWorkspace from "../../../api/api-hooks/workspaces/useUpdateWorkspace";
import useWorkspace from "../../../api/api-hooks/workspaces/useWorkspace";
import useGlobalApp from "../../../api/api-hooks/globalApps/useGlobalApp";
import { CloudApp, GlobalApp } from "../../../types/GlobalInterfaces";
import { AppToggle } from "../../Drawers/Fields/CustomToggle";
import SkeletonLoader from "../../../elements/SkeletonLoader";
import IconButton from "../../../elements/IconButton";
import AppBadge from "../../../elements/AppBadge";

export interface ExtendedGlobalApp extends GlobalApp {
  isSameCloud: boolean;
  isInstalledOnWS: boolean;
  isInstalledOnCloud: boolean;
  isInstalled: boolean;
  isInstalledButNotInWS: boolean;
  isOutdatedSidebar: boolean;
  isOutdated: boolean;
  isCloudApp: boolean;
  buttonDisabled: boolean;
  buttonText: string;
  visibility?: boolean;
  uninstallFunc: () => void;
  installFunc: () => void;
}

export interface ExtendedCloudApp extends Omit<CloudApp, "Icon"> {
  isSameCloud: boolean;
  isInstalledOnWS: boolean;
  isInstalledOnCloud: boolean;
  isInstalled: boolean;
  isInstalledButNotInWS: boolean;
  isOutdatedSidebar: boolean;
  isOutdated: boolean;
  isCloudApp: boolean;
  buttonDisabled: boolean;
  buttonText: string;
  uninstallFunc?: () => void;
  installFunc?: () => void;
  Icon?: string;
}

interface Props {
  /**
   * Contains the app data along with other information (version, outdated, etc.)
   */
  app: ExtendedGlobalApp | ExtendedCloudApp | any;
  /**
   * Function to redirect to the app page.
   */
  onClick?: (e: MouseEvent) => void;
  /**
   * Function to update outdated apps.
   */
  onUpdate?: () => void;
  updating?: boolean;
  updateDisabled?: boolean;
  options?: ReactNode;
  /**
   * An boolean that should only be enabled on the Workspace Settings page. (Enables the "Shoud"/"Hide" options)
   */
  workspaceVisibility?: boolean;
  isGlobalApp?: boolean;
}

const AppCard = ({
  app,
  onClick,
  onUpdate,
  updating,
  updateDisabled,
  options,
  workspaceVisibility = false,
  isGlobalApp = false,
}: Props) => {
  const whisperRef = useRef<WhisperInstance>(null);
  const { updateWorkspace } = useUpdateWorkspace();
  const { workspace } = useWorkspace();
  let { globalApp, loadingApp } = useGlobalApp(app.id, !isGlobalApp);

  if (loadingApp) return <SkeletonLoader width={"378px"} height="230px" />;

  return (
    <div
      onClick={onClick ? onClick : undefined}
      className="tw:flex tw:flex-col tw:justify-start tw:cursor-pointer tw:rounded"
      style={{
        boxShadow: "0px 1px 8px 2px rgba(0, 0, 0, 0.15)",
        minWidth: "300px",
        width: "100%",
        maxWidth: "378px",
        height: "auto",
      }}
    >
      <div className="tw:flex tw:items-center tw:justify-between tw:pr-2 tw:border-bottom tw:border-superlight">
        <div
          className="tw:flex tw:items-center tw:px-3"
          style={{ gap: "6px", paddingTop: 10, paddingBottom: 10 }}
        >
          {app.Icon && typeof app?.Icon === "object" ? (
            <img
              alt="app-image"
              src={app?.Icon?.PublicURL}
              style={{ width: 25, height: 25 }}
              className="tw:rounded-sm tw:object-contain"
            />
          ) : app.Icon && typeof app?.Icon === "string" ? (
            <img
              alt="app-image"
              src={app?.Icon}
              style={{ width: 25, height: 25 }}
              className="rounded-sm"
            />
          ) : null}
          <span
            className="tw:text-primary tw:font-semibold"
            style={{ fontSize: 12 }}
          >
            {app.Name}
          </span>
          {isGlobalApp && (
            <AppBadge
              fontSize={10}
              backgroundColor={app?.isOutdated ? "#FFDEBF" : "#81E3A6"}
              color={app?.isOutdated ? "#FF8A1C" : "#229F50"}
            >
              v{globalApp?.Version}
            </AppBadge>
          )}
        </div>
        {options && (
          <>
            <Whisper
              preventOverflow
              placement="autoVerticalStart"
              controlId=""
              trigger="click"
              ref={whisperRef}
              onClose={() => null}
              speaker={
                <Popover
                  className="sidebar-popover"
                  full
                  onClick={(e: any) => {
                    e.stopPropagation();
                    whisperRef.current?.close();
                  }}
                >
                  {options}
                </Popover>
              }
            >
              <Button
                size="sm"
                outline
                color="secondary"
                className="btn-icon-only"
                onClick={(e) => e.stopPropagation()}
                style={{
                  height: "1.5rem",
                  width: "1.5rem",
                }}
              >
                <i
                  className="mdi mdi-dots-horizontal"
                  style={{ fontSize: 20 }}
                />
              </Button>
            </Whisper>
          </>
        )}
        {onUpdate && !options && !app.isSameCloud && (
          // Another option is: mdi-cloud-download-outline
          <IconButton
            icon="mdi mdi-tray-arrow-down"
            onClick={(e: any) => {
              e.stopPropagation();
              onUpdate();
            }}
            loading={updating}
            disabled={updateDisabled}
          />
        )}
      </div>

      {typeof app?.CoverImage === "string" && (
        <img
          alt="app-cover-image"
          src={app?.CoverImage}
          style={{ height: "125px", objectFit: "cover" }}
        />
      )}
      {typeof app?.CoverImage === "object" && app?.CoverImage?.PublicURL && (
        <img
          alt="app-cover-image"
          src={app?.CoverImage?.PublicURL}
          style={{ height: "125px", objectFit: "cover" }}
        />
      )}
      {isGlobalApp && !app?.CoverImage && (
        <div className="tw:mx-3 tw:flex tw:justify-center tw:items-center tw:min-h-32 tw:bg-ultralight tw:rounded-md">
          <i
            style={{ fontSize: 48 }}
            className="mdi mdi-image-off-outline tw:text-gray-200"
          />
        </div>
      )}
      {app?.Summary ? (
        <div className="tw:px-3 tw:w-full tw:py-3 tw:flex tw:items-center">
          <span className="tw:text-[13px] tw:line-clamp-2 tw:text-dark tw:leading-[19.5px]">
            {app?.Summary}
          </span>
        </div>
      ) : (
        <div className="tw:py-2"></div>
      )}
      {(app?.MSTags || globalApp?.Rating) && (
        <div className="tw:flex tw:flex-row tw:items-center tw:justify-between tw:pb-3 tw:px-3 tw:w-full">
          <div className="tw:flex tw:flex-row tw:items-center tw:gap-2 tw:flex-wrap">
            {!!app?.MSTags ? (
              app?.MSTags?.map((tag: any) => {
                return (
                  <AppBadge
                    key={tag.Title}
                    fontSize={12}
                    borderRadius={4}
                    className="tw:m-0 tw:font-medium"
                    color="#424342"
                  >
                    {tag.Title}
                  </AppBadge>
                );
              })
            ) : (
              <div className="tw:h-8"></div>
            )}
          </div>

          {isGlobalApp && globalApp?.Rating && (
            <Rate
              defaultValue={globalApp?.Rating || 0}
              color="orange"
              size="xs"
              allowHalf
              readOnly
            />
          )}
        </div>
      )}

      {workspaceVisibility && (
        <div
          className="tw:flex tw:justify-end tw:mr-2 tw:pb-2"
          onClick={(e) => e.stopPropagation()}
        >
          <AppToggle
            value={globalApp?.visibility || false}
            handleChange={(e) => {
              e.stopPropagation();
              updateWorkspace({
                payload: {
                  AppsVisibility: {
                    ...workspace?.AppsVisibility,
                    [app.id]: e.target.checked,
                  },
                },
              });
            }}
            checkedOption="Show"
            unCheckedOption="Hide"
          />
        </div>
      )}
    </div>
  );
};

export default AppCard;
